import { useEffect, useState, useMemo, useCallback } from "react";
import { usePerspective, useWasm } from "../../contexts/app/provider";
import { useGetRawParquetQuery } from "../../services/perspective_api/__generated__/perspective_server-types";
import {
  parseParquetData,
  type FullParsedParquetData,
} from "../../utils/parquet";
import { MUIFlatTable } from "../universal_mui_table";
import { getTableData } from "../../utils/mui_flat_table_data";
import { LoadingState } from "../transformed_custom_view/viewer/LoadingState";
import { ErrorState } from "../transformed_custom_view/viewer/ErrorState";

export const AsIsViewerContainer = (props: {
  datasetName: string;
  folder: string;
  setId: string | null;
  format: string;
}) => {
  const { datasetName, folder, setId, format } = props;
  const { perspectiveApiService } = usePerspective();
  const { wasmInitialized, wasmError } = useWasm();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<FullParsedParquetData | null>(
    null
  );
  const [sortConfig, setSortConfig] = useState<
    Array<{ key: string; direction: "asc" | "desc"; index: number }>
  >([]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setParsedData(null);
  }, [datasetName, folder, setId, format]);

  const handleSort = useCallback(
    (key: string, e?: React.MouseEvent<HTMLTableCellElement>) => {
      setSortConfig((prev) => {
        const isShiftPressed = e?.shiftKey || false;
        const existingIndex = prev.findIndex((s) => s.key === key);
        const existingSort = existingIndex >= 0 ? prev[existingIndex] : null;

        // If shift not pressed, start with empty array (single column sort)
        const baseConfig = isShiftPressed ? [...prev] : [];

        if (existingSort) {
          // Column is already sorted - toggle direction or remove
          const newDirection = existingSort.direction === "asc" ? "desc" : null;

          if (newDirection) {
            const index = isShiftPressed ? existingIndex : 0;
            baseConfig[index] = {
              key,
              direction: newDirection,
              index,
            };
            return baseConfig;
          }

          return baseConfig.filter((_, idx) => idx !== existingIndex);
        }

        // New column - add as ascending
        return [
          ...baseConfig,
          { key, direction: "asc", index: baseConfig.length },
        ];
      });
    },
    []
  );

  const { loading: queryLoading, refetch } = useGetRawParquetQuery({
    client: perspectiveApiService.getClient(),
    variables: {
      datasetName,
      folder,
      setId: setId ?? null,
      format,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const parquetDataString = data.getRawParquet;
      if (!parquetDataString) {
        setError("No parquet data found");
        setLoading(false);
        return;
      }
      parseParquetData(parquetDataString, false)
        .then((parsed) => {
          setParsedData(parsed as FullParsedParquetData);
          setLoading(false);
        })
        .catch((error) => {
          console.error("AsIsViewerContainer: error", error);
          setError(error.message);
          setLoading(false);
          setParsedData(null);
        });
    },
    onError: (error) => {
      console.error("AsIsViewerContainer: error", error);
      setLoading(false);
      setError(null);
    },
  });

  // Refetch when variables change
  useEffect(() => {
    refetch();
  }, [datasetName, folder, setId, format, refetch]);

  // Memoize table data to only recalculate when dependencies change
  const tableData = useMemo(() => {
    if (!parsedData) return null;
    return getTableData({
      arrowTable: parsedData.arrowTable,
      columns: parsedData.columns,
      sortConfig,
      onSort: handleSort,
    });
  }, [parsedData, sortConfig, handleSort]);

  // Show WASM error if initialization failed
  if (wasmError) {
    return <ErrorState error={wasmError} />;
  }

  if (loading || queryLoading || !wasmInitialized || !parsedData) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!tableData) {
    return <LoadingState />;
  }

  const { columns, getRow, rowCount } = tableData;

  return (
    <MUIFlatTable
      columns={columns}
      getRow={getRow}
      rowCount={rowCount}
      sortConfig={sortConfig}
    />
  );
};
